// RefPublic.jsx — Page publique d'une association (white-label, /asso/:slug)
// Direction éditoriale : titres Fraunces, layout asymétrique, espacement généreux.
// La couleur dynamique vient de la config asso (couleur_principale).
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Calendar, MapPin, CheckCircle2, Gift, Coins, Sparkles, Loader2, ArrowRight,
  Users, Building2, Mail, Phone, Globe,
} from 'lucide-react';
import EditorCredit from '@/components/EditorCredit';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RefPublic() {
  const params = useParams();
  const slug = params.slug || 'ref-figeac';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/asso/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F8] grid place-items-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#94A3B8]" />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-[#F9F9F8] grid place-items-center px-4">
        <div className="bg-white border border-[#E2E8F0] rounded-3xl p-9 text-center max-w-md">
          <h1 className="font-display text-3xl font-bold text-[#0F172A] mb-3">
            Association <span className="italic font-medium text-[#475569]">introuvable.</span>
          </h1>
          <p className="text-sm text-[#475569] mb-6">L'asso "{slug}" n'existe pas ou n'est plus accessible.</p>
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#0F172A] hover:bg-[#0047FF] text-white font-bold text-sm transition-colors"
          >
            Retour à l'accueil <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  const { asso, evenements, featured = [], stats = null } = data;
  const couleur = asso.couleur_principale || '#00C2D7';
  const couleurSoft = couleur + '15';
  const tarification = asso.config?.tarification || {
    free_first: { label: '1ère réunion offerte', prix_cts: 0 },
    per_meeting: { label: 'À la réunion', prix_cts: 2000 },
    annual_1x: { label: 'Annuel (1×)', prix_cts: 15000 },
    annual_3x: { label: 'Annuel (3×)', prix_cts: 5000, count: 3 },
  };

  return (
    <div
      className="min-h-screen bg-[#F9F9F8] text-[#0F172A] font-sans antialiased"
      style={{ '--brand': couleur }}
      data-testid="ref-public-page"
    >
      {/* HERO ÉDITORIAL */}
      <section className="relative overflow-hidden text-white" style={{ background: `linear-gradient(135deg, ${couleur} 0%, ${shade(couleur, -28)} 100%)` }}>
        <div className="absolute inset-0 opacity-20" aria-hidden style={{ backgroundImage: 'radial-gradient(circle at 25% 30%, white 0%, transparent 45%), radial-gradient(circle at 80% 80%, white 0%, transparent 45%)' }} />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-8 py-20 sm:py-28 grid lg:grid-cols-12 gap-10 items-center">

          {/* Colonne texte */}
          <div className="lg:col-span-8">
            {asso.logo_url ? (
              <img src={asso.logo_url} alt={asso.nom} className="w-20 h-20 rounded-2xl bg-white p-2 mb-7 object-contain shadow-lg" />
            ) : (
              <div className="w-20 h-20 rounded-2xl bg-white grid place-items-center font-display font-black text-3xl mb-7 shadow-lg" style={{ color: couleur }}>
                {asso.nom?.[0]?.toUpperCase()}
              </div>
            )}
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-[10px] font-bold uppercase tracking-[0.2em] mb-6">
              <Sparkles className="w-3 h-3" />
              {asso.type || 'Réseau professionnel'}{asso.ville ? ` · ${asso.ville}` : ''}
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[0.98] mb-6">
              {asso.nom}
            </h1>
            <p className="text-lg sm:text-xl text-white/85 leading-relaxed max-w-2xl mb-10">
              {asso.description_publique || 'Un réseau d\'entrepreneurs et de chefs d\'entreprise qui se rencontrent régulièrement pour partager, apprendre et faire du business ensemble.'}
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => { setSelectedEvent(null); setShowForm(true); }}
                className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-white font-bold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                style={{ color: couleur }}
                data-testid="cta-first-meeting"
              >
                <Gift className="w-4 h-4" />
                Ma 1ère réunion offerte
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </button>
              <a
                href="#tarifs"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl font-bold border border-white/30 hover:bg-white/10 backdrop-blur-sm transition-colors"
              >
                Voir les tarifs
              </a>
            </div>
          </div>

          {/* Colonne carte rejoindre (desktop) */}
          <div className="hidden lg:block lg:col-span-4">
            <div className="bg-white/12 backdrop-blur-md border border-white/20 rounded-3xl p-7">
              <Users className="w-8 h-8 mb-4 opacity-90" />
              <div className="font-display text-3xl font-bold tracking-tight leading-tight mb-3">
                Rejoignez-<span className="italic font-medium opacity-90">nous.</span>
              </div>
              <p className="text-sm text-white/75 leading-relaxed">
                Découvrez le club lors d'une 1ère réunion sans engagement.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PROCHAINES RÉUNIONS */}
      {evenements.length > 0 && (
        <section className="bg-white border-y border-[#E2E8F0]">
          <div className="max-w-6xl mx-auto px-6 lg:px-8 py-20 sm:py-24">
            <div className="max-w-xl mb-12">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] mb-3" style={{ color: couleur }}>Agenda</div>
              <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#0F172A] leading-[1.02]">
                Prochaines <span className="italic font-medium" style={{ color: couleur }}>réunions.</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {evenements.map((e) => {
                const date = new Date(e.date_debut);
                return (
                  <div key={e.id} className="group bg-white border border-[#E2E8F0] rounded-3xl p-6 transition-all hover:shadow-lg hover:-translate-y-1" data-testid={`event-card-${e.id}`}>
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-14 h-14 rounded-2xl text-white grid place-items-center flex-col leading-none shrink-0 shadow-sm" style={{ background: couleur }}>
                        <div className="font-display text-xl font-black">{date.getDate()}</div>
                        <div className="text-[9px] font-bold uppercase tracking-wider">{date.toLocaleDateString('fr-FR', { month: 'short' })}</div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-display font-bold text-[#0F172A] text-base leading-tight mb-1.5 truncate">{e.titre}</div>
                        <div className="flex items-center gap-1 text-xs text-[#475569]">
                          <MapPin className="w-3 h-3" /> {e.lieu || '—'}
                        </div>
                      </div>
                    </div>
                    {e.description && <p className="text-sm text-[#475569] leading-relaxed line-clamp-2 mb-4">{e.description}</p>}
                    <button
                      onClick={() => { setSelectedEvent(e); setShowForm(true); }}
                      className="w-full text-sm font-bold py-2.5 rounded-xl text-white transition-opacity hover:opacity-90"
                      style={{ background: couleur }}
                      data-testid={`event-register-${e.id}`}
                    >
                      S'inscrire →
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* STATS PREUVE SOCIALE */}
      {stats && (stats.entreprises > 0 || stats.reunions_annee > 0) && (
        <section className="bg-[#F9F9F8]" data-testid="public-stats">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 py-14 grid grid-cols-3 gap-6 text-center">
            <Stat value={stats.entreprises} label="entreprises membres" color={couleur} testid="stat-entreprises" />
            <Stat value={stats.secteurs} label="secteurs représentés" color={couleur} testid="stat-secteurs" />
            <Stat value={stats.reunions_annee} label="réunions cette année" color={couleur} testid="stat-reunions" />
          </div>
        </section>
      )}

      {/* MEMBRES EN AVANT */}
      {featured.length > 0 && (
        <section className="bg-white border-y border-[#E2E8F0]">
          <div className="max-w-6xl mx-auto px-6 lg:px-8 py-20 sm:py-24">
            <div className="max-w-xl mb-12">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] mb-3" style={{ color: couleur }}>Le réseau</div>
              <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#0F172A] leading-[1.02] mb-4">
                Découvrez <span className="italic font-medium" style={{ color: couleur }}>nos membres.</span>
              </h2>
              <p className="text-base text-[#475569] leading-relaxed">
                Des entrepreneurs de tous secteurs, prêts à échanger, conseiller et collaborer.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="featured-members">
              {featured.map((m) => (
                <a
                  key={m.id}
                  href={m.site_web ? (m.site_web.startsWith('http') ? m.site_web : `https://${m.site_web}`) : undefined}
                  target={m.site_web ? '_blank' : undefined}
                  rel="noreferrer"
                  className={`group bg-white border border-[#E2E8F0] rounded-2xl p-5 flex items-start gap-3 transition-all ${m.site_web ? 'hover:border-[#94A3B8] hover:-translate-y-0.5 cursor-pointer' : 'cursor-default'}`}
                  data-testid={`featured-member-${m.id}`}
                  onClick={(e) => { if (!m.site_web) e.preventDefault(); }}
                >
                  {m.logo_url ? (
                    <img src={m.logo_url} alt={m.nom_entreprise} className="w-14 h-14 rounded-xl object-cover bg-[#F1F5F9] shrink-0" />
                  ) : (
                    <div className="w-14 h-14 rounded-xl grid place-items-center text-white font-display font-black shrink-0" style={{ background: couleur }}>
                      {m.nom_entreprise?.[0]?.toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <h3 className="font-display font-bold text-[#0F172A] text-base leading-tight truncate">{m.nom_entreprise}</h3>
                    {m.metier && <div className="text-xs text-[#475569] font-medium truncate mt-0.5">{m.metier}</div>}
                    <div className="text-[10px] uppercase tracking-[0.14em] font-bold mt-1.5" style={{ color: couleur }}>
                      {m.secteur}{m.ville ? ` · ${m.ville}` : ''}
                    </div>
                    {m.description && <p className="text-xs text-[#475569] mt-2 line-clamp-2 leading-relaxed">{m.description}</p>}
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TARIFS */}
      <section id="tarifs" className="bg-[#F9F9F8]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 py-20 sm:py-24">
          <div className="max-w-xl mb-12">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] mb-3" style={{ color: couleur }}>Tarifs</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#0F172A] leading-[1.02] mb-4">
              3 formules <span className="italic font-medium" style={{ color: couleur }}>pour rejoindre.</span>
            </h2>
            <p className="text-base text-[#475569] leading-relaxed">
              Commencez gratuitement. Choisissez ensuite la formule qui vous convient.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <PricingCard
              icon={Gift}
              title="Découverte"
              price="Gratuit"
              subtitle="1ère réunion offerte"
              features={['Accès à 1 réunion', 'Présentation du club', 'Pas d\'engagement']}
              cta="Réserver"
              onClick={() => { setSelectedEvent(null); setShowForm(true); }}
              color={couleur} colorSoft={couleurSoft} testid="pricing-free"
            />
            <PricingCard
              icon={Coins}
              title="À la carte"
              price={`${(tarification.per_meeting?.prix_cts || 2000) / 100} €`}
              subtitle="par réunion"
              features={['Réservation à l\'unité', 'Repas inclus*', 'Idéal pour tester']}
              cta="Voir le calendrier"
              onClick={() => document.querySelector('#tarifs')?.scrollIntoView()}
              color={couleur} colorSoft={couleurSoft} testid="pricing-per-meeting"
            />
            <PricingCard
              icon={Sparkles}
              title="Annuel"
              price={`${(tarification.annual_1x?.prix_cts || 15000) / 100} €`}
              subtitle={`/an · ou 3× ${(tarification.annual_3x?.prix_cts || 5000) / 100} €`}
              features={['Accès à toutes les réunions', 'Annuaire membres', 'Gazette + messagerie', 'Repas inclus']}
              cta="Devenir membre"
              onClick={() => { setSelectedEvent(null); setShowForm(true); }}
              color={couleur} colorSoft={couleurSoft} testid="pricing-annual"
              highlighted
            />
          </div>
          <p className="text-center text-xs text-[#94A3B8] mt-8">
            * Conditions précisées lors de l'inscription. Paiement en 3× sans frais possible via Stripe.
          </p>
        </div>
      </section>

      {/* CONTACT */}
      <section className="bg-white border-t border-[#E2E8F0]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 py-16 sm:py-20">
          <div className="bg-[#F9F9F8] border border-[#E2E8F0] rounded-3xl p-8 text-center">
            <div className="w-12 h-12 rounded-2xl grid place-items-center mx-auto mb-5" style={{ background: couleurSoft, color: couleur }}>
              <Building2 className="w-6 h-6" />
            </div>
            <h3 className="font-display text-2xl sm:text-3xl font-bold text-[#0F172A] mb-5 leading-tight">
              Une <span className="italic font-medium" style={{ color: couleur }}>question ?</span>
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              {asso.email_contact && (
                <a href={`mailto:${asso.email_contact}`} className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white border border-[#E2E8F0] hover:border-[#94A3B8] transition-colors">
                  <Mail className="w-4 h-4 text-[#94A3B8]" />
                  <span className="text-[#0F172A] font-medium truncate">{asso.email_contact}</span>
                </a>
              )}
              {asso.telephone && (
                <a href={`tel:${asso.telephone}`} className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white border border-[#E2E8F0] hover:border-[#94A3B8] transition-colors">
                  <Phone className="w-4 h-4 text-[#94A3B8]" />
                  <span className="text-[#0F172A] font-medium">{asso.telephone}</span>
                </a>
              )}
              {asso.site_web && (
                <a href={asso.site_web} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white border border-[#E2E8F0] hover:border-[#94A3B8] transition-colors">
                  <Globe className="w-4 h-4 text-[#94A3B8]" />
                  <span className="text-[#0F172A] font-medium">Site officiel</span>
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      <EditorCredit variant="light" />

      {showForm && (
        <InscriptionModal
          slug={slug}
          asso={asso}
          evenement={selectedEvent}
          evenements={evenements}
          onClose={() => setShowForm(false)}
        />
      )}
    </div>
  );
}

function Stat({ value, label, color, testid }) {
  return (
    <div data-testid={testid}>
      <div className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight" style={{ color }}>
        {value}
      </div>
      <div className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.18em] text-[#475569] mt-2">{label}</div>
    </div>
  );
}

function PricingCard({ icon: Ic, title, price, subtitle, features, cta, onClick, color, colorSoft, testid, highlighted }) {
  return (
    <div
      className={`relative bg-white border rounded-3xl p-7 transition-all hover:-translate-y-1 ${highlighted ? 'shadow-xl' : 'border-[#E2E8F0] hover:shadow-lg'}`}
      style={highlighted ? { borderColor: color, borderWidth: '2px' } : {}}
      data-testid={testid}
    >
      {highlighted && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-[0.18em] text-white" style={{ background: color }}>
          Recommandé
        </div>
      )}
      <div className="w-11 h-11 rounded-xl grid place-items-center mb-4" style={{ background: colorSoft, color }}>
        <Ic className="w-5 h-5" />
      </div>
      <h3 className="font-display text-xl font-bold text-[#0F172A]">{title}</h3>
      <div className="font-display text-4xl font-black my-3 tracking-tight" style={{ color }}>{price}</div>
      <div className="text-xs text-[#475569] mb-5">{subtitle}</div>
      <ul className="space-y-2 mb-6">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-[#0F172A]">
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color }} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <button
        onClick={onClick}
        className="w-full text-sm font-bold py-3 rounded-xl text-white transition-opacity hover:opacity-90"
        style={{ background: color }}
      >
        {cta} →
      </button>
    </div>
  );
}

function InscriptionModal({ slug, asso, evenement, evenements, onClose }) {
  const [form, setForm] = useState({
    prenom: '', nom: '', email: '', telephone: '',
    entreprise: '', metier: '',
    evenement_id: evenement?.id || '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const couleur = asso.couleur_principale || '#00C2D7';

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/asso/${slug}/inscription-premiere`, form);
      setSuccess(true);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erreur lors de l\'inscription');
    } finally { setSubmitting(false); }
  };

  if (success) {
    return (
      <div className="fixed inset-0 z-50 bg-[#0F172A]/60 backdrop-blur-md grid place-items-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-9 max-w-md w-full text-center" data-testid="inscription-success">
          <div className="w-16 h-16 rounded-2xl grid place-items-center mx-auto mb-5 text-white" style={{ background: couleur }}>
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <h3 className="font-display text-2xl font-bold text-[#0F172A] mb-3 leading-tight">
            Inscription <span className="italic font-medium" style={{ color: couleur }}>confirmée !</span>
          </h3>
          <p className="text-sm text-[#475569] leading-relaxed mb-7">
            Merci <strong className="text-[#0F172A]">{form.prenom}</strong> ! Un membre du bureau de <strong className="text-[#0F172A]">{asso.nom}</strong> vous contactera sous 48h pour finaliser votre venue à la 1ère réunion (gratuite).
          </p>
          <button onClick={onClose} className="inline-flex items-center justify-center w-full px-5 py-3 rounded-xl bg-[#0F172A] hover:bg-[#0047FF] text-white font-bold text-sm transition-colors" data-testid="inscription-close">
            Fermer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-[#0F172A]/60 backdrop-blur-md grid place-items-start justify-center p-4 sm:p-6 overflow-y-auto"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl shadow-2xl p-7 sm:p-9 max-w-md w-full my-6"
        data-testid="inscription-modal"
      >
        <div className="text-center mb-6">
          <div className="w-12 h-12 rounded-2xl grid place-items-center mx-auto mb-4 text-white" style={{ background: couleur }}>
            <Gift className="w-6 h-6" />
          </div>
          <h3 className="font-display text-2xl font-bold text-[#0F172A] mb-2 leading-tight">
            Ma 1ère <span className="italic font-medium" style={{ color: couleur }}>réunion offerte</span>
          </h3>
          <p className="text-xs text-[#475569]">Aucun engagement · Le bureau vous recontacte sous 48h.</p>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input required type="text" value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} placeholder="Prénom *" className="ap-input" data-testid="insc-prenom" />
            <input required type="text" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} placeholder="Nom *" className="ap-input" data-testid="insc-nom" />
          </div>
          <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email *" className="ap-input" data-testid="insc-email" />
          <input type="tel" value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} placeholder="Téléphone (optionnel)" className="ap-input" data-testid="insc-tel" />
          <input type="text" value={form.entreprise} onChange={(e) => setForm({ ...form, entreprise: e.target.value })} placeholder="Entreprise (optionnel)" className="ap-input" data-testid="insc-entreprise" />
          <input type="text" value={form.metier} onChange={(e) => setForm({ ...form, metier: e.target.value })} placeholder="Métier (optionnel)" className="ap-input" data-testid="insc-metier" />
          {evenements.length > 0 && (
            <select value={form.evenement_id} onChange={(e) => setForm({ ...form, evenement_id: e.target.value })} className="ap-input" data-testid="insc-event">
              <option value="">Choisir une réunion (optionnel)</option>
              {evenements.map((e) => {
                const dateStr = new Date(e.date_debut).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long' });
                return (
                  <option key={e.id} value={e.id}>{`${dateStr} — ${e.titre || e.nom}`}</option>
                );
              })}
            </select>
          )}
          <textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Un message pour le bureau (optionnel)" className="ap-input" data-testid="insc-message" />
        </div>
        <div className="flex gap-2 justify-end mt-7">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl text-sm font-semibold text-[#475569] hover:bg-[#F1F5F9] transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-6 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50 shadow-md transition-opacity hover:opacity-90"
            style={{ background: couleur }}
            data-testid="insc-submit"
          >
            {submitting ? 'Envoi…' : 'Réserver ma place →'}
          </button>
        </div>
      </form>
    </div>
  );
}

function shade(hex, percent) {
  const n = parseInt(hex.replace('#', ''), 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  const adj = (c) => Math.max(0, Math.min(255, Math.round(c + c * (percent / 100))));
  return `rgb(${adj(r)}, ${adj(g)}, ${adj(b)})`;
}
